import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from '@headlessui/react';
import { FaCheck } from 'react-icons/fa';
import userService from '../services/userService';
import accountService from '../services/accountService';
import { useUser } from '../contexts/UserContext';
import toast from 'react-hot-toast';
import {
  DEFAULT_AVAILABLE_LANGUAGES,
  LANGUAGE_OPTIONS,
  filterEnabledLanguages,
} from '../config/languages';

const languages = LANGUAGE_OPTIONS;

interface LanguageSwitcherProps {
  className?: string;
  showLabel?: boolean;
  compact?: boolean;
}

export default function LanguageSwitcher({ className = '', showLabel = false, compact = false }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation('common');
  const { user, refreshUser } = useUser();
  const [isChanging, setIsChanging] = useState(false);

  // Get available languages from user's account settings
  const availableLanguages = useMemo(() => {
    if (!user) {
      return DEFAULT_AVAILABLE_LANGUAGES;
    }

    const accountLanguages =
      user.corporateAccount?.availableLanguages ||
      user.individualAccount?.availableLanguages;

    return filterEnabledLanguages(accountLanguages || DEFAULT_AVAILABLE_LANGUAGES);
  }, [user?.corporateAccount?.availableLanguages, user?.individualAccount?.availableLanguages]);

  // Filter languages based on available languages
  const filteredLanguages = languages.filter(lang => availableLanguages.includes(lang.code));
  
  // Ensure current language is always available (fallback)
  const currentLanguageCode = i18n.language;
  const currentLanguage = filteredLanguages.find(lang => lang.code === currentLanguageCode) || 
                         languages.find(lang => lang.code === currentLanguageCode) || 
                         filteredLanguages[0] || 
                         languages[0];
  
  // If current language is not available, switch to first available
  useEffect(() => {
    if (filteredLanguages.length > 0 && !filteredLanguages.find(lang => lang.code === currentLanguageCode)) {
      i18n.changeLanguage(filteredLanguages[0].code);
      localStorage.setItem('i18nextLng', filteredLanguages[0].code);
    }
  }, [availableLanguages, currentLanguageCode, filteredLanguages, i18n]);

  const handleLanguageChange = async (languageCode: string) => {
    if (languageCode === i18n.language) {
      return;
    }

    setIsChanging(true);
    
    try {
      const previousLanguage = i18n.language;
      
      // Change language immediately for better UX
      await i18n.changeLanguage(languageCode);
      
      // Save to localStorage
      localStorage.setItem('i18nextLng', languageCode);

      // If user is logged in, save preference to backend
      if (user?.id) {
        try {
          await userService.updateLanguagePreference(user.id, languageCode);
          
          // AUTO-SWITCH CALENDAR FEATURE:
          // For accounts with Fa language enabled AND Jalali calendar:
          // - When switching to ANY language other than Farsi: auto-switch to Gregorian
          // - When switching back to Farsi: auto-switch back to Jalali
          const accountAvailableLanguages = user.corporateAccount?.availableLanguages || user.individualAccount?.availableLanguages || [];
          const currentCalendarType = user.corporateAccount?.calendarType || user.individualAccount?.calendarType;
          
          // Check if account has Fa enabled
          const hasFaEnabled = accountAvailableLanguages.includes('fa');
          const isJalaliCalendar = currentCalendarType === 'JALALI';
          const isGregorianCalendar = currentCalendarType === 'GREGORIAN';
          const switchingToFa = languageCode === 'fa';
          const switchingToNonFa = languageCode !== 'fa';
          
          let shouldSwitchCalendar = false;
          let newCalendarType: 'GREGORIAN' | 'JALALI' | null = null;
          
          // Case 1: Account has Fa enabled + Jalali calendar, switching to non-Fa → switch to Gregorian
          if (hasFaEnabled && isJalaliCalendar && switchingToNonFa) {
            shouldSwitchCalendar = true;
            newCalendarType = 'GREGORIAN';
            console.log('🗓️ Auto-switching calendar from Jalali to Gregorian (switching away from Fa)');
          }
          // Case 2: Account has Fa enabled + Gregorian calendar, switching to Fa → switch to Jalali
          else if (hasFaEnabled && isGregorianCalendar && switchingToFa) {
            shouldSwitchCalendar = true;
            newCalendarType = 'JALALI';
            console.log('🗓️ Auto-switching calendar from Gregorian to Jalali (switching to Fa)');
          }
          
          if (shouldSwitchCalendar && newCalendarType) {
            console.log('   Account has Fa enabled:', hasFaEnabled);
            console.log('   Current calendar:', currentCalendarType);
            console.log('   New language:', languageCode);
            console.log('   New calendar:', newCalendarType);
            console.log('   User ID:', user.id);
            console.log('   User account type:', user.corporateAccount ? 'corporate' : user.individualAccount ? 'individual' : 'none');
            
            try {
              const result = await accountService.updateCurrentAccountCalendarType(user, newCalendarType);
              console.log(`✅ Calendar automatically switched to ${newCalendarType}`, result);
              
              // Refresh user context to get updated account data
              await refreshUser();
              
              // Reload page to ensure all dates are reformatted with new calendar type
              const calendarName = newCalendarType === 'GREGORIAN' ? 'Gregorian' : 'Jalali';
              toast.success(
                t('common:messages.saved') + ' - ' + 
                `Calendar automatically switched to ${calendarName}. Refreshing...`,
                { duration: 2000 }
              );
              
              // Reload after a short delay to allow toast to be seen
              setTimeout(() => {
                window.location.reload();
              }, 1500);
              
              return; // Exit early since we're reloading
            } catch (calendarError: any) {
              console.error('❌ Error auto-switching calendar:', calendarError);
              console.error('   Error message:', calendarError?.message);
              console.error('   Error response:', calendarError?.response);
              // Show error toast so user knows something went wrong
              toast.error('Failed to auto-switch calendar. Please update manually in settings.');
              // Continue to normal refresh - language change still succeeded
            }
          }
          
          // Refresh user context to get updated account data (if no calendar change or error)
          await refreshUser();
          
          // Show success message only if we didn't auto-switch calendar
          if (!shouldSwitchCalendar) {
            toast.success(t('common:messages.saved'));
          }
        } catch (error) {
          console.error('Error saving language preference to backend:', error);
          // Don't show error toast for this - language is still changed locally
        }
      } else {
        // User not logged in, just show success for language change
        toast.success(t('common:messages.saved'));
      }
    } catch (error) {
      console.error('Error changing language:', error);
      toast.error(t('common:messages.error'));
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <Menu as="div" className={`relative inline-block text-left z-[9999] ${className}`}>
      <Menu.Button
        className={
          compact
            ? 'h-7 w-7 flex items-center justify-center text-[10px] font-bold text-[#6D6D72] rounded-[8px] active:bg-white/80 focus:outline-none disabled:opacity-50 transition-colors relative z-[9999]'
            : 'flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors min-w-[60px] justify-center relative z-[9999]'
        }
        disabled={isChanging}
      >
        <span className={compact ? 'text-[10px] font-bold' : 'text-sm font-bold'}>
          {currentLanguage.code.toUpperCase()}
        </span>
        {showLabel && <span className="text-sm ml-1">- {currentLanguage.name}</span>}
      </Menu.Button>

      <Menu.Items className="absolute right-0 z-[9999] w-44 mt-2 origin-top-right bg-white border border-gray-200 rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
        <div className="py-1">
          {filteredLanguages.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No languages available
            </div>
          ) : (
            filteredLanguages.map((language) => (
            <Menu.Item key={language.code}>
              {({ active }) => (
                <button
                  onClick={() => handleLanguageChange(language.code)}
                  className={`${
                    active ? 'bg-gray-50' : ''
                  } ${
                    language.code === i18n.language ? 'bg-green-50 border-l-4 border-green-500' : ''
                  } group flex items-center justify-between w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors`}
                  disabled={isChanging}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900 w-8">{language.code.toUpperCase()}</span>
                    <span className="text-gray-600">-</span>
                    <span className="font-medium">{language.name}</span>
                  </div>
                  {language.code === i18n.language && (
                    <FaCheck className="w-4 h-4 text-green-600" />
                  )}
                </button>
              )}
            </Menu.Item>
            ))
          )}
        </div>
      </Menu.Items>
    </Menu>
  );
}

